import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { CheckSquare, Copy, Lightbulb, Plus, Search, X, ChevronLeft } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNotes } from "./use-notes";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import { toast } from "sonner";
import { DrawerClose } from "../../../../components/ui/drawer";
import { useTranslation } from "react-i18next";

const TakeawaysRunningActivity = ({
  processId,
  isMobile,
  onCollapse,
  notes: propNotes,
  loading: propLoading,
  error: propError,
  addNote: propAddNote,
  editNote: propEditNote,
  removeNote: propRemoveNote,
}: {
  processId: string;
  isMobile: boolean;
  onCollapse?: () => void;
  notes?: any[];
  loading?: boolean;
  error?: string | null;
  addNote?: (note: any) => Promise<any>;
  editNote?: (noteId: string, updates: any) => Promise<void>;
  removeNote?: (noteId: string) => Promise<void>;
}) => {
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [newNoteType, setNewNoteType] = useState<"action" | "insight" | null>(null);
  const [newNoteContent, setNewNoteContent] = useState("");
  const editingTextareaRef = React.useRef<HTMLTextAreaElement>(null);
  const newNoteTextareaRef = React.useRef<HTMLTextAreaElement>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  // Use props if provided, otherwise fall back to useNotes hook (for backwards compatibility)
  const fallbackHook = useNotes({
    processId,
    searchQuery: debouncedQuery,
    onError: (error: any) => {
      toast(error);
    },
    onSuccess: (message: any) => {
      toast(message);
    },
  });

  const notes = propNotes ?? fallbackHook.notes;
  const loading = propLoading ?? fallbackHook.loading;
  const error = propError ?? fallbackHook.error;
  const addNote = propAddNote ?? fallbackHook.addNote;
  const editNote = propEditNote ?? fallbackHook.editNote;
  const removeNote = propRemoveNote ?? fallbackHook.removeNote;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300); // 300ms debounce

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  const toggleNoteType = async (noteId: string) => {
    const note = notes.find((n) => n._id === noteId);
    if (!note || !note._id) return;

    const newType = note.type === "action" ? "insight" : "action";
    await editNote(note._id, { type: newType });
  };

  const saveNoteContent = async (noteId: string, content: string) => {
    if (content.trim() === "") {
      await removeNote(noteId);
      setEditingNoteId(null);
    } else {
      await editNote(noteId, { content });
      setEditingNoteId(null);
    }
  };

  const deleteTakeAway = async (noteId: string) => {
    await removeNote(noteId);
    if (editingNoteId === noteId) {
      setEditingNoteId(null);
    }
  };

  const handleCreateNote = (type: "action" | "insight") => {
    // If already adding a note of different type, save it first
    if (newNoteType && newNoteType !== type) {
      handleSaveNewNote();
    }

    // Show inline input for new note
    setNewNoteType(type);
    setNewNoteContent("");
    // Focus the textarea and scroll to bottom after it renders
    setTimeout(() => {
      newNoteTextareaRef.current?.focus();
      // Scroll to bottom of the scroll container
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
      }
    }, 100);
  };

  const savingNewNote = React.useRef(false);

  const handleSaveNewNote = async () => {
    // Prevent multiple simultaneous saves
    if (savingNewNote.current) return;

    if (!newNoteContent.trim() || !newNoteType) {
      // Cancel if no content
      setNewNoteType(null);
      setNewNoteContent("");
      return;
    }

    savingNewNote.current = true;
    const newNote = await addNote({
      type: newNoteType,
      content: newNoteContent.trim(),
      assignedTo: null,
      createdBy: "current-user",
    });

    savingNewNote.current = false;

    if (newNote && newNote._id) {
      // Clear the new note form
      setNewNoteType(null);
      setNewNoteContent("");
    }
  };

  const handleCancelNewNote = () => {
    setNewNoteType(null);
    setNewNoteContent("");
  };

  const renderNotesList = (
    notesList: any[],
    title: string,
    icon: React.ReactNode,
    emptyMessage: string,
    isActionItem: boolean
  ) => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <h4 className="text-sm font-medium">{title}</h4>
          <span className="text-xs text-muted-foreground">
            ({notesList.length})
          </span>
        </div>
        <Button
          variant="outline"
          onClick={() => handleCreateNote(isActionItem ? "action" : "insight")}
          disabled={loading}
        >
          <Plus className="h-4 w-4 mr-1" />
          {t("add")}
        </Button>
      </div>

      <div ref={scrollContainerRef} className="space-y-2 max-h-[9rem] overflow-y-auto pr-1">
        {/* Existing notes */}
        {notesList.map((note: any) => {
          const isEditing = editingNoteId === note._id;
          return (
            <div
              key={note._id}
              className="flex items-center gap-2 p-2 rounded-md border group hover:bg-muted/50 overflow-x-auto"
            >
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0 mt-1"
                onClick={() => toggleNoteType(note._id)}
                title={note.type === "action" ? "Action Item" : "Insight"}
                disabled={loading}
              >
                {note.type === "action" ? (
                  <CheckSquare className="h-4 w-4 text-primary" />
                ) : (
                  <Lightbulb className="h-4 w-4 text-amber-500" />
                )}
              </Button>

              <div className="flex-1 space-y-2">
                {isEditing ? (
                  <Textarea
                    ref={editingTextareaRef}
                    defaultValue={note.content}
                    placeholder={t("new_note_field_placeholder")}
                    className="resize-none min-h-[60px]"
                    autoFocus
                    onBlur={(e) => {
                      saveNoteContent(note._id, e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        saveNoteContent(note._id, e.currentTarget.value);
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        if (!note.content.trim()) {
                          // Delete empty note on escape
                          deleteTakeAway(note._id);
                        } else {
                          setEditingNoteId(null);
                        }
                      }
                    }}
                    disabled={loading}
                  />
                ) : (
                  <div
                    className="text-sm cursor-pointer hover:text-primary transition-colors"
                    onClick={() => setEditingNoteId(note._id)}
                  >
                    {note.content || (
                      <span className="text-muted-foreground italic">
                        {t("new_note_field_placeholder")}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => deleteTakeAway(note._id)}
                title="Delete note"
                disabled={loading}
              >
                <X className="h-4 w-4 text-muted-foreground hover:text-destructive" />
              </Button>
            </div>
          );
        })}

        {/* Inline new note input - show at bottom when adding */}
        {newNoteType && newNoteType === (isActionItem ? "action" : "insight") && (
          <div className="flex items-start gap-2 p-2 rounded-md border-2 border-primary/30 bg-primary/5">
            <div className="h-6 w-6 shrink-0 mt-1 flex items-center justify-center">
              {newNoteType === "action" ? (
                <CheckSquare className="h-4 w-4 text-primary" />
              ) : (
                <Lightbulb className="h-4 w-4 text-amber-500" />
              )}
            </div>

            <div className="flex-1 space-y-1">
              <Textarea
                ref={newNoteTextareaRef}
                value={newNoteContent}
                onChange={(e) => setNewNoteContent(e.target.value)}
                placeholder={t("new_note_field_placeholder")}
                className="resize-none min-h-[60px] border-0 bg-transparent focus-visible:ring-0 p-0"
                autoFocus
                onBlur={handleSaveNewNote}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSaveNewNote();
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    handleCancelNewNote();
                  }
                }}
                disabled={loading}
              />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{t("press_enter_to_save")}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 px-2 text-xs"
                  onClick={handleCancelNewNote}
                >
                  {t("cancel")}
                </Button>
              </div>
            </div>
          </div>
        )}

        {notesList.length === 0 && !(newNoteType && newNoteType === (isActionItem ? "action" : "insight")) && (
          <div className="text-center py-3 text-muted-foreground">
            <p className="text-sm">{emptyMessage}</p>
          </div>
        )}
      </div>
    </div>
  );

  const copyTakeawaysToClipboard = async () => {
    const actionItems = notes
      .filter(
        (note: any) => note.type === "action" && note.content.trim() !== ""
      )
      .map((note: any) => `- ${note.content}`);

    const insights = notes
      .filter(
        (note: any) => note.type === "insight" && note.content.trim() !== ""
      )
      .map((note: any) => `- ${note.content}`);

    let notesText = "";

    if (actionItems.length > 0) {
      notesText += `📝 ${t("action_items")}:\n${actionItems.join("\n")}\n\n`;
    }

    if (insights.length > 0) {
      notesText += `💡 ${t("insights")}:\n${insights.join("\n")}`;
    }

    if (!notesText.trim()) {
      toast(t("no_notes_copy_message"));
      return;
    }

    try {
      await navigator.clipboard.writeText(notesText.trim());
      toast.info(t("copy_notes_toast_message"));
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
      toast.error("Failed to copy notes");
    }
  };

  const actionNotes = notes.filter((note) => note.type === "action");
  const insightNotes = notes.filter((note) => note.type === "insight");

  return (
    <div className="flex flex-col h-full">
      {/* Header - different for mobile drawer vs desktop panel */}
      {isMobile ? (
        <div className="flex justify-between items-center p-4 pb-2">
          <h3 className="text-sm font-medium">{t("your_takeaways")}</h3>
          <DrawerClose>
            <X className="h-4 w-4" />
          </DrawerClose>
        </div>
      ) : (
        <div className="sticky top-0 bg-background border-b px-4 py-3 z-10 flex items-center justify-between">
          <h3 className="text-base font-semibold">{t("your_takeaways")}</h3>
          {onCollapse && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onCollapse}
              title="Collapse panel"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}

      {/* Content area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t("search_notes")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 focus-visible:ring-0"
        />
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-4 text-muted-foreground">
          <p className="text-sm">{t("loading_notes")}</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="text-center py-4 text-red-500">
          <p className="text-sm">{error}</p>
        </div>
      )}
      <Button
        variant="outline"
        size="sm"
        onClick={copyTakeawaysToClipboard}
        className="flex items-center gap-2"
      >
        <Copy className="h-4 w-4" />
        {t("copy_all_notes")}
      </Button>

      {/* Actions Section */}
      {renderNotesList(
        actionNotes,
        t("action_items"),
        <CheckSquare className="h-4 w-4 text-primary" />,
        t("no_action_items_yet"),
        true
      )}

      {/* Insights Section */}
      {renderNotesList(
        insightNotes,
        t("insights"),
        <Lightbulb className="h-4 w-4 text-amber-500" />,
        t("no_insights_yet"),
        false
      )}

      <div className="p-3 bg-muted rounded-lg text-sm">
        <p>{t("takeaways_bottom_message")}</p>
      </div>
      </div>
    </div>
  );
};

export default TakeawaysRunningActivity;
